import React, { useState, useEffect } from 'react';
import { fetchWithAuth } from '../utils/apiHelper';
import moment from 'moment';
import 'moment/locale/es';
import { FaCalendarAlt, FaSearch } from 'react-icons/fa';
import { BiLoaderAlt } from 'react-icons/bi';

moment.locale('es');

const TurnosCancelados = () => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [cancellations, setCancellations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Initialize default date range to the current month
  useEffect(() => {
    const today = new Date();
    const defaultStartDate = new Date(today.getFullYear(), today.getMonth(), 1)
      .toISOString()
      .split('T')[0];
    const defaultEndDate = new Date(today.getFullYear(), today.getMonth() + 1, 0)
      .toISOString()
      .split('T')[0];
    setStartDate(defaultStartDate);
    setEndDate(defaultEndDate);
    fetchCancellations(defaultStartDate, defaultEndDate);
  }, []);

  // Fetch canceled appointments from API
  const fetchCancellations = async (start, end) => {
    setLoading(true);
    try {
      const response = await fetchWithAuth(
        `/turnos/cancelados?fechaInicio=${start}&fechaFin=${end}`
      );
      setCancellations(response || []);
    } catch (error) {
      console.error('Error al obtener turnos cancelados:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
    fetchCancellations(start, end);
  };

  // Filter by client name
  const filteredCancellations = cancellations.filter((cancel) =>
    `${cancel.cliente_nombre} ${cancel.cliente_apellido}`
      .toLowerCase()
      .includes(searchQuery.toLowerCase())
  );

  // Helper to format time without seconds/decimals
  const formatHour = (timeString) => {
    if (!timeString) return 'No especificado';
    // Attempt to parse "HH:mm:ss" or "HH:mm:ss.xxx"
    return moment(timeString, ['HH:mm:ss.SSSSSS', 'HH:mm:ss']).format('HH:mm');
  };

  return (
    <div className="relative mx-auto max-w-7xl px-4 md:px-8 py-8">
      {/* Background with a subtle radial gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-red-50 to-red-100 -z-10" />

      {/* Page Title */}
      <h2 className="text-center text-3xl md:text-4xl font-extrabold text-red-700 mb-10">
        Turnos Cancelados
      </h2>

      {/* Filter Panel */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8 bg-white/80 shadow-xl ring-1 ring-red-200 rounded-xl px-4 py-6">
        {/* Date Range: Start */}
        <div className="flex items-center space-x-2">
          <FaCalendarAlt className="text-red-500" />
          <label className="text-sm text-gray-700 font-semibold">Inicio</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => handleDateChange(e.target.value, endDate)}
            className="p-2 border border-red-300 rounded focus:ring-2 focus:ring-red-500 text-sm"
          />
        </div>

        {/* Date Range: End */}
        <div className="flex items-center space-x-2">
          <FaCalendarAlt className="text-red-500" />
          <label className="text-sm text-gray-700 font-semibold">Fin</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => handleDateChange(startDate, e.target.value)}
            className="p-2 border border-red-300 rounded focus:ring-2 focus:ring-red-500 text-sm"
          />
        </div>

        {/* Search Input */}
        <div className="flex items-center space-x-2 w-full md:w-auto">
          <FaSearch className="text-gray-500" />
          <input
            type="text"
            placeholder="Buscar por cliente"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 p-2 border border-red-300 rounded focus:ring-2 focus:ring-red-500 text-sm"
          />
        </div>

        {/* Submit / Fetch Button */}
        <button
          onClick={() => fetchCancellations(startDate, endDate)}
          disabled={loading}
          className="inline-flex items-center justify-center px-4 py-2 font-semibold text-sm text-white bg-red-600 hover:bg-red-700 rounded shadow focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-75 disabled:cursor-not-allowed"
        >
          {loading ? <BiLoaderAlt className="animate-spin mr-2" /> : 'Buscar'}
        </button>
      </div>

      {/* Loading State */}
      {loading && (
        <p className="text-center text-gray-700 animate-pulse mb-4">
          Cargando turnos cancelados...
        </p>
      )}

      {/* Content */}
      {!loading && filteredCancellations.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCancellations.map((cancel, index) => {
            const formattedHour = formatHour(cancel.turno_hora);
            const formattedPrice = cancel.servicio_precio
              ? Number(cancel.servicio_precio).toLocaleString()
              : null;
            return (
              <div
                key={index}
                className="relative bg-white rounded-xl border-l-4 border-red-400 p-6 shadow-md hover:shadow-lg transition-shadow"
              >
                {/* Subtle decorative circle behind heading */}
                <div className="absolute top-0 right-0 p-2">
                  <div className="w-10 h-10 rounded-full bg-red-50" />
                </div>

                {/* Client Full Name */}
                <h3 className="text-xl font-bold text-red-700 mb-2">
                  {cancel.cliente_nombre} {cancel.cliente_apellido || ''}
                </h3>

                {/* Service Details */}
                <p className="text-sm text-gray-700 mb-1">
                  <span className="font-semibold">Servicio:</span>{' '}
                  <span className="text-gray-800">
                    {cancel.servicio_nombre}
                  </span>
                </p>

                {/* Scheduled Time of Appointment */}
                <p className="text-sm text-gray-700 mb-1">
                  <span className="font-semibold">Hora prevista:</span>{' '}
                  {formattedHour}
                </p>

                {/* Employee Details */}
                <p className="text-sm text-gray-700 mb-1">
                  <span className="font-semibold">Empleado:</span>{' '}
                  {cancel.empleado_nombre} {cancel.empleado_apellido || ''}
                </p>

                {/* Cancellation Reason */}
                <p className="text-sm text-gray-700 mb-1">
                  <span className="font-semibold">Motivo:</span>{' '}
                  {cancel.motivo_cancelacion || 'Sin motivo'}
                </p>

                {/* Branch details */}
                <p className="text-sm text-gray-700 mb-3">
                  <span className="font-semibold">Sucursal:</span>{' '}
                  {cancel.sucursal_nombre || 'No especificado'}
                </p>

                {/* Cancellation Date (no hour shown) */}
                <p className="text-xs text-gray-500 italic">
                  Cancelado el{' '}
                  {moment(cancel.fecha_cancelacion).format('D [de] MMMM [de] YYYY')}
                </p>
              </div>
            );
          })}
        </div>
      ) : !loading ? (
        <p className="text-center text-gray-500">
          No se encontraron turnos cancelados.
        </p>
      ) : null}
    </div>
  );
};

export default TurnosCancelados;
